import { useSyncExternalStore } from 'react';
import {
  type MotionPreference,
  MOTION_STORAGE_KEY,
  readOsReducedMotion,
  resolveReducedMotion,
} from '@/lib/motion';

function getCachedPreference(): MotionPreference {
  if (typeof window === 'undefined') return 'system';
  const cached = localStorage.getItem(MOTION_STORAGE_KEY);
  if (cached === 'full' || cached === 'reduced' || cached === 'system') {
    return cached;
  }
  return 'system';
}

function subscribeToOsMotion(onStoreChange: () => void) {
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
  mq.addEventListener('change', onStoreChange);
  return () => mq.removeEventListener('change', onStoreChange);
}

function subscribeToPreference(onStoreChange: () => void) {
  const handler = () => onStoreChange();
  window.addEventListener('motion-preference-change', handler);
  return () => window.removeEventListener('motion-preference-change', handler);
}

function getOsReducedSnapshot() {
  return readOsReducedMotion();
}

function getPreferenceSnapshot(): MotionPreference {
  return getCachedPreference();
}

/** Notify listeners when the in-app motion preference changes (Phase 6). */
export function notifyMotionPreferenceChange() {
  window.dispatchEvent(new Event('motion-preference-change'));
}

/**
 * Returns whether effective reduced motion is active.
 * Combines OS `prefers-reduced-motion` with cached in-app preference (Phase 6 wires auth).
 */
export function useReducedMotion(): boolean {
  const osReduced = useSyncExternalStore(
    subscribeToOsMotion,
    getOsReducedSnapshot,
    () => false,
  );

  const preference = useSyncExternalStore(
    subscribeToPreference,
    getPreferenceSnapshot,
    () => 'system' as MotionPreference,
  );

  return resolveReducedMotion(preference, osReduced);
}
